import axios from "axios";
import { getAnonId } from "./anon";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach token from localStorage if present (cookie-based also works)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("decal_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Always attach the anon id so the server can stitch the anon session to
  // its cookie even if the cookie was stripped (mobile safari etc).
  try {
    config.headers = config.headers || {};
    config.headers["X-Anon-ID"] = getAnonId();
  } catch {
    /* noop */
  }
  return config;
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  // FastAPI-style { msg } (validation errors) AND our app-level
  // { code, message } shape both used here. Without the `message`
  // fallback, the toast prints "[object Object]".
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;
  return "Something went wrong. Please try again.";
}

// Turn any axios error into the most useful user-facing string we can.
// Prefers the server's structured `detail` field, then falls back to
// status-aware hints (502/504 → gateway timeout, no status → network
// error, 5xx → generic backend error). Use this anywhere a try/catch
// would otherwise toast a vague message.
export function formatAxiosError(e) {
  if (!e) return "Something went wrong. Please try again.";
  const detail = e.response?.data?.detail;
  if (detail != null) return formatApiErrorDetail(detail);
  const status = e.response?.status;
  if (status === 502 || status === 503 || status === 504) {
    return `Server timed out (HTTP ${status}). Please try again in a few seconds.`;
  }
  if (status === 413) {
    return "Request payload too large.";
  }
  if (!status) {
    return "Couldn't reach the server. Check your connection and try again.";
  }
  if (status >= 500) {
    return `Server error (HTTP ${status}). Please try again — if it persists, contact support@decallabs.co.za.`;
  }
  if (status === 401) {
    return "Incorrect email or password.";
  }
  if (status === 429) {
    return "Too many attempts. Please wait a few minutes and try again.";
  }
  return e.message || "Something went wrong. Please try again.";
}
